import Group from 'components/utils/Group'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import BundleOptionTileGroup from './BundleAndSaveTiles/BundleOptionTileGroup'
import {
  BundleOfferPackageOptionView,
  getAvailableBundlePackages,
  getOffersAvailableRates,
  getBundledPackages,
  areCalendarsLoading,
} from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SupportNumberButton from 'components/Luxkit/Button/SupportNumberButton'
import { buildBundlePackagesViewOrder } from 'lib/bundleAndSave/buildBundlePackagesViewOrder'
import { InView } from 'react-intersection-observer'
import { scrollToOfferPackageOptions, scrollToOfferPackageOptionsOfferType } from 'analytics/optimizely/events'
import * as Analytics from 'analytics/analytics'
import BundleAndSaveCallUs from './BundleAndSaveCallUs'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'

const ConstrainedContent = styled.div`
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
`

interface Props {
  filteredBundlePackageId?: string
  packageOptions: { [bundlePackageId: string]: BundleOfferPackageOptionView }
  bundledPackages: { [bundlePackageId: string]: App.Package[] }
  offersAvailableRates?: App.OfferAvailableRates[]
  fetching?: boolean;
  availableForPurchase: boolean;
}

function BundleAndSaveOptionsSection({
  filteredBundlePackageId,
  packageOptions,
  bundledPackages,
  offersAvailableRates,
  fetching,
  availableForPurchase,
}: Props) {
  const hasRateError = (offersAvailableRates ?? []).some((p) => !!p?.error)

  const filteredPackageOptions = buildBundlePackagesViewOrder({
    filteredBundlePackageId,
    packageOptions,
    bundledPackages,
    fetching,
    availableForPurchase,
  })

  const handlePackageOptionsInView = useCallback((inView: boolean) => {
    if (inView) {
      Analytics.track('scroll-to-offer-package-options', scrollToOfferPackageOptions())
      Analytics.track('scroll-to-offer-package-options', scrollToOfferPackageOptionsOfferType('bundle_and_save'))
    }
  }, [])

  return (
    <ConstrainedContent>
      <Group direction="vertical" gap={20}>
        {availableForPurchase && (
          <Group fullWidth direction="vertical" horizontalAlign="center" gap={12}>
            <BodyText variant="large">Select a bundle type</BodyText>
          </Group>
        )}
        {hasRateError && <MessageBanner
          kind="warning"
          heading="The reservation system is temporarily down"
          description="Our team are working on it now. Please check back later or call customer service anytime, 24 hours, 7 days a week."
          trailingContent={<SupportNumberButton />}
        />}
        {!hasRateError && (
          <Group direction="vertical" gap={32}>
            {filteredPackageOptions.map((bundlePackageId, index) => <>
              <BundleOptionTileGroup
                key={bundlePackageId}
                bundledPackages={bundledPackages[bundlePackageId] ?? []}
                packageView={packageOptions[bundlePackageId]}
                fetching={fetching}
                availableForPurchase={availableForPurchase}
              />
              {index === 0 && <InView rootMargin="0px" triggerOnce onChange={handlePackageOptionsInView} />}
            </>)}
          </Group>
        )}

        {isBundleAndSaveV2Enabled() && <BundleAndSaveCallUs />}
      </Group>
    </ConstrainedContent>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    filteredBundlePackageId: !state.activeFilter ? state.filteredBundlePackageId : undefined,
    bundledPackages: getBundledPackages(state),
    packageOptions: getAvailableBundlePackages(state, appState),
    offersAvailableRates: getOffersAvailableRates(appState, state),
    fetching: areCalendarsLoading(appState, state),
  }
})(BundleAndSaveOptionsSection)
